import { ThemeProvider } from "@emotion/react";
import { LoadingIcon } from "@hl/communities-app/lib/assets/icons.generated";
import { useAuth } from "@hl/shared-features/lib/features/auth/AuthContext";
import { UserNavbar } from "@hl/shared-features/lib/features/auth/UserNavbar";
import { Box, Center } from "@mantine/core";

import { PageViewDocument } from "~/features/pages/queries/page.graphql.generated";

import { Background } from "../../components/blocks/Background";

import { LockedPage } from "./LockedPage";
import { UnlockedPage, usePage } from "./PageView";

type EmebedProps = {
  pageSlug?: string;
};

export const PageViewEmbed: React.FC<EmebedProps> = ({ pageSlug }) => {
  const { loading, pageTheme, data, error } = usePage({ pageSlug });

  if (loading) {
    return <Loader />;
  }

  if (error?.graphQLErrors[0]?.extensions.status == 404) {
    return <div>Not found</div>;
  }
  if (error || !data) {
    return <div>Unknown error</div>;
  }

  if (data?.pageView === null) {
    return <div>Page not available</div>;
  }

  const gate = data?.pageView.gate;
  if (data.pageView.isLocked && !gate) {
    return <div>Page data is corrupted</div>;
  }

  return (
    <ThemeProvider theme={pageTheme}>
      <Background>
        <EmbedHeader />
        <Center>
          <Box
            sx={{
              maxWidth: "600px",
              width: "90%",
            }}
          >
            {data.pageView.isLocked ? (
              <LockedPage
                creatorAddress={data.pageView.creator?.evmAddresses[0]}
                creatorEns={data.pageView.creator?.ensName}
                lockedMessage={gate?.lockedMessage}
                unlockGateResult={data?.pageView?.unlockGateResult ?? undefined}
                refetchOnRequirementChange={[PageViewDocument]}
              />
            ) : (
              <UnlockedPage page={data.pageView.page!} />
            )}
          </Box>
        </Center>
      </Background>
    </ThemeProvider>
  );
};

const Loader = () => (
  <Box
    sx={{
      display: "flex",
      height: 100,
      justifyContent: "center",
      alignItems: "center",
    }}
  >
    <LoadingIcon />
  </Box>
);

const EmbedHeader = () => {
  const { authenticated } = useAuth();
  return (
    <Box sx={{ textAlign: "right" }} p="sm">
      {authenticated && <UserNavbar />}
    </Box>
  );
};
